import React, { useState } from 'react';
import { Card, IconButton } from '../ui';
import { Trash2, Edit2, X, Check, GripVertical } from 'lucide-react';

const OfferingCard = ({
  offering,
  index,
  onSave,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnter,
  onDragLeave,
  isDragging,
  isDraggedOver,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(offering);

  // Check if form data has changed
  const hasChanges = () => {
    return (
      formData.name !== offering.name ||
      formData.description !== offering.description ||
      formData.page_url !== offering.page_url
    );
  };

  const handleSave = (id, formData) => {
    onSave(id, formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(offering);
    setIsEditing(false);
  };

  return (
    <div className={`transition-all duration-200 ${isDraggedOver && !isDragging ? 'mb-32' : ''}`}>
      <Card
        className={`relative bg-transparent border-1 shadow-none transition-all cursor-move ${
          isDragging ? 'opacity-50 scale-95' : 'opacity-100'
        } ${
          isDraggedOver && !isDragging
            ? 'border-primary-500 border-2 border-dashed'
            : 'border-gray-300'
        }`}
        draggable={!isEditing}
        onDragStart={(e) => !isEditing && onDragStart(e, index)}
        onDragOver={(e) => !isEditing && onDragOver(e, index)}
        onDragEnter={(e) => !isEditing && onDragEnter(e, index)}
        onDragLeave={(e) => !isEditing && onDragLeave(e, index)}
        onDrop={(e) => !isEditing && onDrop(e, index)}
      >
        <Card.Body className='p-4'>
          <div className='space-y-3'>
            <div className='flex items-center justify-between mb-2'>
              <div className='flex items-center gap-2'>
                {!isEditing && (
                  <GripVertical className='w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing' />
                )}
                <div className='font-medium text-sm text-gray-700'>Offering #{index + 1}</div>
              </div>
              <div className='flex items-center gap-2'>
                {isEditing ? (
                  <>
                    <IconButton
                      icon={Check}
                      tooltip='Save offering'
                      onClick={() => handleSave(formData.id, formData)}
                      variant={'primary'}
                      disabled={!hasChanges()}
                    />
                    <IconButton
                      icon={X}
                      tooltip='Cancel editing'
                      onClick={handleCancel}
                      variant='default'
                    />
                  </>
                ) : (
                  <>
                    <IconButton
                      icon={Edit2}
                      tooltip='Edit offering'
                      onClick={() => setIsEditing(true)}
                      variant='primary'
                    />
                    {onDelete && (
                      <IconButton
                        icon={Trash2}
                        tooltip='Delete offering'
                        onClick={() => onDelete(formData.id)}
                        variant='danger'
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Name {isEditing && '*'}
              </label>
              {isEditing ? (
                <input
                  type='text'
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                  required
                />
              ) : (
                <p className='text-gray-900'>{offering.name}</p>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Description</label>
              {isEditing ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                />
              ) : (
                <p className='text-gray-900'>{offering.description || 'No description'}</p>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Page URL</label>
              {isEditing ? (
                <input
                  type='url'
                  value={formData.page_url || ''}
                  onChange={(e) => setFormData({ ...formData, page_url: e.target.value })}
                  placeholder='https://example.com/offering'
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                />
              ) : (
                <p className='text-gray-900'>
                  {offering.page_url ? (
                    <a
                      href={offering.page_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary-600 hover:underline'
                    >
                      {offering.page_url}
                    </a>
                  ) : (
                    'Not specified'
                  )}
                </p>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OfferingCard;
